/**
 * Command Center — Guardrail rules.
 * Rules are stored per tenant and evaluated by the backend on every test:
 * a failing Blocker locks the next stage, a Guardrail is written into the
 * plan as an accepted risk, a Recommendation is advice only.
 */

import React, { useEffect, useState } from "react";
import { Modal } from "antd";
import { CcStyles, DeleteModal, ErrorBanner, errMsg } from "./components";
import { CatalogCheck, CcLevel, CcMode, Rule, ccApi } from "./api";
import { LEVEL } from "./common";

const ORDER: CcLevel[] = ["rec", "guard", "block"];
const STAGE_LABEL: Record<string, string> = { plan: "Plan", data: "Data", design: "Design", results: "Results" };

export default function CommandCenterRules({ mode }: { mode: CcMode }) {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [catalog, setCatalog] = useState<CatalogCheck[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [check, setCheck] = useState("");
  const [threshold, setThreshold] = useState("");
  const [level, setLevel] = useState<CcLevel>("guard");
  const [both, setBoth] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setRules(null);
    ccApi.rules(mode).then(setRules).catch(err => setError(`Failed to load rules: ${errMsg(err)}`));
    ccApi.catalog().then(c => setCatalog(c.filter(x => x.modes.includes(mode)))).catch(() => {});
  }, [mode]);

  const patch = async (r: Rule, body: { level?: CcLevel; enabled?: boolean }) => {
    setRules(prev => (prev || []).map(x => (x.id === r.id ? { ...x, ...body } : x)));
    try {
      const saved = await ccApi.updateRule(r.id, body);
      setRules(prev => (prev || []).map(x => (x.id === r.id ? saved : x)));
    } catch (err) {
      setError(`Could not update the rule: ${errMsg(err)}`);
      setRules(prev => (prev || []).map(x => (x.id === r.id ? r : x)));
    }
  };

  const remove = async (id: string) => {
    setDeleting(true);
    try {
      await ccApi.deleteRule(id);
      setRules(prev => (prev || []).filter(r => r.id !== id));
    } catch (err) {
      setError(`Delete failed: ${errMsg(err)}`);
    } finally {
      setDeleting(false);
      setConfirmId(null);
    }
  };
  const ruleToDelete = (rules || []).find(r => r.id === confirmId) || null;

  const picked = catalog.find(c => c.key === check);
  const openAdd = () => {
    const first = catalog[0];
    setCheck(first?.key || ""); setThreshold(first?.default != null ? String(first.default) : ""); setLevel("guard"); setBoth(false);
    setAdding(true);
  };
  const valid = !!picked && (!picked.unit || (threshold !== "" && !isNaN(Number(threshold))));
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || !picked) return;
    setSaving(true);
    try {
      const rule = await ccApi.createRule(mode, { check, threshold: picked.unit ? Number(threshold) : null, level, applies_to_both: both });
      setRules(prev => [...(prev || []), rule]);
      setAdding(false);
    } catch (err) {
      setError(`Could not add the rule: ${errMsg(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cc-page" style={{ maxWidth: 1060 }}>
      <CcStyles />
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <h1 className="cc-h1">Guardrail rules</h1>
          <p className="cc-sub">
            {mode === "ml"
              ? "Checked automatically on every test: the plan against the Calendar and US holidays, the uploaded data, the GeoLift design and the readout."
              : "Checked automatically on every test: the plan against the Calendar and US holidays, and the numbers in the partner’s decks."}
            {" "}Click a level to change it.
          </p>
        </div>
        <button className="cc-btn primary" onClick={openAdd} disabled={!catalog.length}>+ Add rule</button>
      </div>

      <ErrorBanner msg={error} onClose={() => setError(null)} />

      <div className="cc-grid-3">
        <div className="cc-card"><span style={{ color: LEVEL.rec.color, fontWeight: 600, fontSize: 13 }}>● Recommended</span><span className="cc-help" style={{ color: "var(--text-secondary)" }}>Advice only. Shown on the test.</span></div>
        <div className="cc-card"><span style={{ color: LEVEL.guard.color, fontWeight: 600, fontSize: 13 }}>● Guardrail</span><span className="cc-help" style={{ color: "var(--text-secondary)" }}>Allowed. Written into the plan as an accepted risk.</span></div>
        <div className="cc-card"><span style={{ color: LEVEL.block.color, fontWeight: 600, fontSize: 13 }}>● Blocker</span><span className="cc-help" style={{ color: "var(--text-secondary)" }}>Locks the next stage until it is fixed.</span></div>
      </div>

      {!rules ? (
        !error && <div className="cc-skel" style={{ height: 360 }} />
      ) : (
        <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", overflowX: "auto" }}>
          <div style={{ minWidth: 760 }}>
            <div style={{ display: "flex", gap: 14, padding: "12px 20px", fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-tertiary)", fontWeight: 600 }}>
              <span style={{ width: 70 }}>Stage</span><span style={{ flex: 1 }}>Rule</span><span style={{ width: 90 }}>Applies to</span><span style={{ width: 130, textAlign: "center" }}>Level</span><span style={{ width: 54 }}>On</span><span style={{ width: 24 }} />
            </div>
            {rules.map(r => {
              const l = LEVEL[r.level];
              return (
                <div key={r.id} className="cc-rrow" style={{ opacity: r.enabled ? 1 : 0.5 }}>
                  <span style={{ width: 70, fontSize: 12, color: "var(--text-secondary)" }}>{STAGE_LABEL[r.stage || ""] || "—"}</span>
                  <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 13.5 }}>{r.label}</span>
                    <span className="cc-help">{r.description}{!r.is_default && <span style={{ color: "var(--violet)" }}> · custom</span>}</span>
                  </span>
                  <span style={{ width: 90, fontSize: 12, color: "var(--text-secondary)" }}>{r.mode === "both" ? "Both menus" : "This menu"}</span>
                  <button className="cc-lvl" onClick={() => patch(r, { level: ORDER[(ORDER.indexOf(r.level) + 1) % 3] })} style={{ color: l.color, background: l.bg, border: `1px solid ${l.color}` }}>
                    <span className="cc-dot" style={{ width: 6, height: 6 }} />{l.t}
                  </button>
                  <label style={{ width: 54, display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: "var(--text-secondary)" }}>
                    <input type="checkbox" checked={r.enabled} onChange={e => patch(r, { enabled: e.target.checked })} />
                    {r.enabled ? "On" : "Off"}
                  </label>
                  <span style={{ width: 24 }}>
                    <button className="cc-icon-btn" onClick={() => setConfirmId(r.id)} title="Delete rule" aria-label="Delete rule">✕</button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal open={adding} onCancel={() => setAdding(false)} footer={null} width={540} destroyOnHidden centered maskClosable={false}>
        <form onSubmit={save} style={{ paddingTop: 4, fontFamily: "var(--font-sans)" }}>
          <div style={{ marginBottom: 18, paddingRight: 24 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "var(--text-primary)" }}>Add a guardrail rule</h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>Pick what to check, the limit and how strict it is. It applies to every test from now on.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>Check</span>
              <select className="cc-modal-input" value={check} onChange={e => {
                const c = catalog.find(x => x.key === e.target.value);
                setCheck(e.target.value); setThreshold(c?.default != null ? String(c.default) : "");
              }}>
                {catalog.map(c => <option key={c.key} value={c.key}>{STAGE_LABEL[c.stage]} · {c.label.replace("{t}", "N")}</option>)}
              </select>
              {picked && <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{picked.description}</span>}
            </label>
            {picked?.unit && (
              <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>Limit ({picked.unit})</span>
                <input className="cc-modal-input" type="number" step="any" value={threshold} onChange={e => setThreshold(e.target.value)} style={{ maxWidth: 160 }} />
                <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Rule reads: “{picked.label.replace("{t}", threshold || "N")}”</span>
              </label>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)" }}>Level when it fails</span>
              <div style={{ display: "flex", gap: 8 }}>
                {ORDER.map(lv => (
                  <button key={lv} type="button" className="cc-lvl" onClick={() => setLevel(lv)}
                    style={{ color: LEVEL[lv].color, background: level === lv ? LEVEL[lv].bg : "transparent", border: `1px solid ${level === lv ? LEVEL[lv].color : "var(--border-default)"}` }}>
                    <span className="cc-dot" style={{ width: 6, height: 6 }} />{LEVEL[lv].t}
                  </button>
                ))}
              </div>
            </div>
            {picked && picked.modes.length > 1 && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-primary)", cursor: "pointer" }}>
                <input type="checkbox" checked={both} onChange={e => setBoth(e.target.checked)} />
                Apply in both Command Center menus
              </label>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid var(--border-subtle)", paddingTop: 14, marginTop: 18 }}>
            <button type="button" className="cc-btn" onClick={() => setAdding(false)}>Cancel</button>
            <button type="submit" className={`cc-btn${valid ? " primary" : ""}`} disabled={!valid || saving}>{saving ? "Saving…" : "Add rule"}</button>
          </div>
        </form>
      </Modal>
      {ruleToDelete && (
        <DeleteModal name={ruleToDelete.label} loading={deleting}
          body="This guardrail rule will no longer be checked on any test in this Command Center. This cannot be undone."
          onConfirm={() => remove(ruleToDelete.id)} onCancel={() => setConfirmId(null)} />
      )}
    </div>
  );
}
